import { useState } from "react";
import { X, Plus, Check, Trash2, PencilLine } from "lucide-react";
import { api, UNITS, RAYONS } from "@/lib/api";
import { IMPORT_IDS } from "@/constants/testIds/meal";
import { toast } from "sonner";

const empty = () => ({ name: "", quantity: "", unit: "g", rayon: "Autre", recipe: "" });

export default function ManualEntryModal({ onClose, onDone }) {
  const [current, setCurrent] = useState(empty());
  const [preview, setPreview] = useState([]);
  const [saving, setSaving] = useState(false);

  const addToPreview = () => {
    if (!current.name.trim()) {
      toast.error("Nom d'ingrédient requis");
      return;
    }
    const q = parseFloat(current.quantity);
    if (isNaN(q) || q < 0) {
      toast.error("Quantité invalide");
      return;
    }
    setPreview((p) => [...p, { ...current, quantity: q, _id: Date.now() + Math.random() }]);
    setCurrent(empty());
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addToPreview();
    }
  };

  const removePreview = (id) => setPreview((p) => p.filter((i) => i._id !== id));

  const handleConfirm = async () => {
    if (preview.length === 0) {
      toast.error("Ajoute au moins un ingrédient");
      return;
    }
    setSaving(true);
    try {
      // Group by recipe name
      const byRecipe = {};
      preview.forEach((it) => {
        const r = it.recipe.trim() || "Ajout manuel";
        if (!byRecipe[r]) byRecipe[r] = [];
        byRecipe[r].push({
          name: it.name.trim(),
          quantity: it.quantity,
          unit: it.unit,
          rayon: it.rayon,
        });
      });
      for (const [recipe, ings] of Object.entries(byRecipe)) {
        await api.addRecipe(recipe, ings);
      }
      toast.success(`${preview.length} ingrédient(s) ajouté(s) 🎉`);
      onDone();
    } catch {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-black/40 backdrop-blur-sm">
      <div
        data-testid={IMPORT_IDS.manualModal}
        className="chibi-card w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col chibi-pop"
      >
        <div className="flex items-center justify-between p-6 border-b border-[#E8E2D2]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#E07A5F" }}>
              <PencilLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-xl text-[#4A3F35]">Saisie manuelle</h3>
              <p className="text-sm text-[#4A3F35]/60">Ajoute tes ingrédients un à un (touche Entrée)</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F4F1EA]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Input row */}
          <div className="p-4 rounded-[24px] bg-[#F4F1EA]/50 border-2 border-dashed border-[#E8E2D2]">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <input
                data-testid={IMPORT_IDS.manualName}
                placeholder="Nom ingrédient"
                className="chibi-input md:col-span-4 !py-2"
                value={current.name}
                onChange={(e) => setCurrent({ ...current, name: e.target.value })}
                onKeyDown={handleKeyDown}
                autoFocus
              />
              <input
                data-testid={IMPORT_IDS.manualQty}
                type="number"
                step="0.1"
                placeholder="Qté"
                className="chibi-input md:col-span-2 !py-2"
                value={current.quantity}
                onChange={(e) => setCurrent({ ...current, quantity: e.target.value })}
                onKeyDown={handleKeyDown}
              />
              <select
                data-testid={IMPORT_IDS.manualUnit}
                className="chibi-input md:col-span-2 !py-2"
                value={current.unit}
                onChange={(e) => setCurrent({ ...current, unit: e.target.value })}
              >
                {UNITS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
              <select
                className="chibi-input md:col-span-2 !py-2"
                value={current.rayon}
                onChange={(e) => setCurrent({ ...current, rayon: e.target.value })}
              >
                {RAYONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <button
                data-testid={IMPORT_IDS.manualAdd}
                onClick={addToPreview}
                className="chibi-btn-secondary !py-2 md:col-span-2 inline-flex items-center gap-1 justify-center"
              >
                <Plus className="w-4 h-4" /> Ajouter
              </button>
            </div>
            <input
              data-testid={IMPORT_IDS.manualRecipe}
              placeholder="Nom de la recette (optionnel)"
              className="chibi-input !py-2 mt-3"
              value={current.recipe}
              onChange={(e) => setCurrent({ ...current, recipe: e.target.value })}
              onKeyDown={handleKeyDown}
            />
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wide text-[#4A3F35]/70 mb-2">
                Prévisualisation ({preview.length})
              </h4>
              <div className="space-y-2">
                {preview.map((it) => (
                  <div key={it._id} className="flex items-center gap-3 p-3 rounded-[20px] bg-white border border-[#E8E2D2]">
                    <span className="flex-1 font-medium">{it.name}</span>
                    <span className="text-sm text-[#4A3F35]/70">{it.quantity} {it.unit}</span>
                    <span className="text-xs px-3 py-1 rounded-full" style={{ background: "#A3B18A22", color: "#4A3F35" }}>{it.rayon}</span>
                    {it.recipe && (
                      <span className="text-xs italic text-[#4A3F35]/60">{it.recipe}</span>
                    )}
                    <button onClick={() => removePreview(it._id)} className="p-1 rounded-full hover:bg-[#E07A5F]/10">
                      <Trash2 className="w-4 h-4 text-[#E07A5F]" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-[#E8E2D2] flex gap-3 justify-end">
          <button onClick={onClose} className="chibi-btn-ghost">Annuler</button>
          <button
            data-testid={IMPORT_IDS.manualConfirm}
            onClick={handleConfirm}
            disabled={saving || preview.length === 0}
            className="chibi-btn-primary inline-flex items-center gap-2"
          >
            <Check className="w-5 h-5" />
            {saving ? "Ajout..." : `Valider (${preview.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
